/* eslint-disable no-restricted-syntax */
import { sortBy } from 'lodash-es';
import { normalizeToRange, ROUNDING_DIRECTIONS } from './numbers';

function getHighestColorInRange(colorMap, value) {
  if (!colorMap) return null;
  const values = Object.keys(colorMap)
    .map((key) => parseFloat(key))
    .sort((a, b) => a - b);
  const lookupValue = parseFloat(value);
  if (Number.isNaN(lookupValue)) return null;

  let highestKey = values[0];
  for (const currentValue of values) {
    highestKey = currentValue;
    if (currentValue >= lookupValue) break;
  }

  return colorMap[highestKey];
}

export function getFormattedStats(
  stats,
  cArr,
  hasDash = false,
  chartInterval = null,
  chartStart = null,
) {
  let chartDistribution = [];
  if (chartInterval && chartStart) {
    // Roll distribution into chart-specific intervals.
    let isFirst = true;
    for (
      let current = chartStart;
      current <= stats.stats.max + chartInterval;
      current += chartInterval
    ) {
      if (isFirst) {
        const firstChunk = stats.distribution.filter(
          (dist) => dist.label <= current,
        );
        const firstCombinedValue = firstChunk.reduce(
          (acc, cur) => acc + cur.value,
          0,
        );
        chartDistribution.push({
          label: current,
          value: firstCombinedValue,
        });
        isFirst = false;
      } else {
        const lastInterval = current - chartInterval;
        const chunk = stats.distribution.filter(
          (dist) => dist.label > lastInterval && dist.label <= current,
        );
        const combinedValue = chunk.reduce((acc, cur) => acc + cur.value, 0);
        chartDistribution.push({
          label: current,
          value: combinedValue,
        });
      }
    }
  } else {
    chartDistribution = stats.distribution;
  }

  chartDistribution = sortBy(chartDistribution, 'label');

  const formattedStats = {
    features: stats.features || [],
    stats: stats.stats,
    chart: {
      values: chartDistribution
        .map((item) => ({
          label: hasDash ? item.label.split(' - ')[1] : `${item.label}`,
          value: item.value,
        }))
        .filter((item) => item.value > 0),
    },
  };
  const colors = formattedStats.chart.values.map((i) => {
    const color = getHighestColorInRange(cArr, i.label);
    return color
      ? `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${color[3] / 255})`
      : 'rgba(51, 51, 51, 1)';
  });
  formattedStats.colors = colors;
  return formattedStats;
}

/**
 * Gets an adjustment preview for a given set of stats.
 * @param {Object} stats - The stats to adjust.
 * @param {Number} fieldAdjustmentFactor - The field-level adjustment factor.
 * @param {Array} groupAdjustments - The group adjustments.
 * @param {Number} lowerBound - The lower bound allowable by the stats (default 0).
 * @param {Number} upperBound - The upper bound allowable by the stats (default Infinity).
 * @param {Number} roundingIncrement - The rounding increment for the labels.
 * @param {Number} chartInterval - The interval between x labels in the chart.
 * @param {Number} chartStart - The starting point for the chart.
 * @returns {Object} - The preview stats.
 */
export function getAdjustmentPreview(
  stats,
  fieldAdjustmentFactor,
  groupAdjustments,
  lowerBound = 0,
  upperBound = Infinity,
  roundingIncrement = 10,
  chartInterval = null,
  chartStart = null,
) {
  const statsPreview = {
    distribution: [],
    features: [],
    stats: {
      min: 0,
      max: 0,
      avg: 0,
      totalAcres: (stats.stats || {}).totalAcres || 0,
    },
  };
  // Adjust stats.
  const adjustedStats = (stats.distribution || []).map((item) => {
    const parsedItemLabel = parseInt(item.label, 10);
    let adjustment = 0;
    const foundAdjustment = groupAdjustments.find(
      (ga) => ga.minValue <= parsedItemLabel && ga.maxValue >= parsedItemLabel,
    );
    if (foundAdjustment) {
      adjustment = parseInt(foundAdjustment.adjustmentFactor, 10);
    }

    let label = parsedItemLabel + adjustment;
    if (label < lowerBound) label = lowerBound;
    if (label > upperBound) label = upperBound;

    return {
      originalLabel: parsedItemLabel,
      label: normalizeToRange(label, roundingIncrement, ROUNDING_DIRECTIONS.UP),
      value: item.value,
    };
  });

  // Combine distinct values for distribution.
  const combinedAdjustedDictionary = adjustedStats.reduce(
    (acc, cur) => ({
      ...acc,
      [cur.label]: (acc[cur.label] || 0) + cur.value,
    }),
    {},
  );
  statsPreview.distribution = Object.keys(combinedAdjustedDictionary).map(
    (key) => ({
      label: parseInt(key, 10),
      value: combinedAdjustedDictionary[key],
    }),
  );

  // Old to new dictionary for feature mapping.
  const oldToNewDictionary = adjustedStats.reduce(
    (acc, cur) => ({
      ...acc,
      [cur.originalLabel]: cur.label,
    }),
    {},
  );

  // Map features.
  statsPreview.features = stats.features.map((feature) => ({
    ...feature,
    properties: {
      ...feature.properties,
      TGT_RATE:
        oldToNewDictionary[feature.properties.TGT_RATE] ||
        feature.properties.TGT_RATE,
    },
  }));

  // Calculate stats.
  const labels = statsPreview.distribution.map((dist) => dist.label);
  const values = statsPreview.distribution.reduce(
    (acc, cur) => acc + cur.value,
    0,
  );
  statsPreview.stats.min = Math.min(...labels);
  statsPreview.stats.max = Math.max(...labels);
  const total = statsPreview.distribution.reduce(
    (acc, cur) => acc + cur.label * cur.value,
    0,
  );
  const weightedAverage = total / values;
  statsPreview.stats.avg = weightedAverage;

  if (chartInterval && (chartStart || chartStart === 0)) {
    // Roll distribution into chart-specific intervals.
    let isFirst = true;
    const chartDistribution = [];
    for (
      let current = chartStart;
      current <= upperBound;
      current += chartInterval
    ) {
      if (isFirst) {
        const firstChunk = statsPreview.distribution.filter(
          (dist) => dist.label <= current,
        );
        const firstCombinedValue = firstChunk.reduce(
          (acc, cur) => acc + cur.value,
          0,
        );
        if (firstCombinedValue > 0) {
          chartDistribution.push({
            label: current,
            value: firstCombinedValue,
          });
        }
        isFirst = false;
      } else {
        const lastInterval = current - chartInterval;
        const chunk = statsPreview.distribution.filter(
          (dist) => dist.label > lastInterval && dist.label <= current,
        );
        const combinedValue = chunk.reduce((acc, cur) => acc + cur.value, 0);
        if (combinedValue > 0) {
          chartDistribution.push({
            label: current,
            value: combinedValue,
          });
        }
      }
    }

    statsPreview.distribution = chartDistribution;
  }

  return statsPreview;
}

export const groupBy = (array, key) =>
  array.reduce((rv, x) => {
    (rv[x[key]] = rv[x[key]] || []).push(x);
    return rv;
  }, {});

export const maxMinAvg = (array = [], key = '') => {
  let max = array[0][key];
  let min = array[0][key];
  let sum = array[0][key];

  for (let i = 1; i < array.length; i += 1) {
    if (array[i][key] > max) max = array[i][key];
    if (array[i][key] < min) min = array[i][key];
    sum += array[i][key];
  }
  return [max, min, sum / array.length];
};

export const totalArrayValue = (array, key) =>
  array.reduce((acc, item) => acc + parseFloat(item[key]), 0);

export function sortDistribution(distribution, sortKey = 'label') {
  return distribution.sort((a, b) => {
    if (a[sortKey] > b[sortKey]) return 1;
    if (a[sortKey] < b[sortKey]) return -1;
    return 0;
  });
}
